import { useEffect, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useModalDismiss } from './use-modal-dismiss';
import {
  AdminRequestError,
  getAmdSettings,
  updateAmdSettings,
  type AmdSettings as AmdSettingsValue,
  type AmdSettingsPatch,
} from './api';

// Twilio MachineDetection valid ranges + Twilio's own defaults (mirror the core's
// AmdSettings changeset). The defaults are the safe recommended starting point.
const SPEECH_THRESHOLD = { min: 1000, max: 6000, default: 2400 } as const;
const SPEECH_END = { min: 500, max: 5000, default: 1200 } as const;

// Our own bound, not Twilio's (mirrors the core's AmdSettings changeset).
const CARD_FALLBACK = { min: 500, max: 30000 } as const;

const HOLD_DEADLINE = { min: 2000, max: 20000 } as const;
const HOLD_PER_SDR = { min: 1, max: 10 } as const;
// Mirrors the core changeset: 30 s keeps a cut call inside its one billed Twilio minute.
const GREETING_RECORD = { min: 0, max: 30 } as const;

// Mirrors the core changeset, which owns these bounds.
const OVERDIAL_PACING = { min: 1, max: 10000 } as const;
const EASTERN_OPEN = { min: 8, max: 12 } as const;

// The cockpit silence gate (mirrors the core changeset). The two audibility
// thresholds are 0–1 floats, exclusive at both ends.
const SILENCE_FLOOR = { min: 5000, max: 120000 } as const;
const SILENCE_QUIET = { min: 2000, max: 60000 } as const;
const SILENCE_LEAD_QUIET = { min: 10000, max: 120000 } as const;
const SILENCE_GRACE = { min: 3000, max: 30000 } as const;
const SILENCE_MODES_FALLBACK = ['off', 'on'] as const;

// Off is nil on the wire and blank in the input, and the dirty check has to agree with
// the field it guards: `String(null)` is "null", which no input can ever equal, so the
// form would offer to save a change nobody made — and saving stamps a config epoch.
const pacingText = (ms: number | null | undefined) => (ms == null ? '' : String(ms));

const INPUT_CLASS = 'mt-1 w-full rounded-md border px-2 py-1.5 text-sm';

// Global answering-machine-detection tuning: the bounded numeric knobs plus a
// guarded async-mode toggle, saved to /bff/admin/settings.
export function AmdSettingsModal({ onClose }: { onClose: () => void }) {
  const dialog = useModalDismiss(onClose);
  const [settings, setSettings] = useState<AmdSettingsValue | null>(null);
  const [threshold, setThreshold] = useState('');
  const [endThreshold, setEndThreshold] = useState('');
  const [cardFallback, setCardFallback] = useState('');
  const [lateMachineProtect, setLateMachineProtect] = useState(true);
  const [greetingRecord, setGreetingRecord] = useState('');
  const [asyncOn, setAsyncOn] = useState(false);
  const [holdUnrouted, setHoldUnrouted] = useState(false);
  const [holdDeadline, setHoldDeadline] = useState('');
  const [holdPerSdr, setHoldPerSdr] = useState('');
  const [overdialPacing, setOverdialPacing] = useState('');
  const [easternOpen, setEasternOpen] = useState('');
  const [silenceMode, setSilenceMode] = useState('off');
  const [silenceFloor, setSilenceFloor] = useState('');
  const [silenceQuiet, setSilenceQuiet] = useState('');
  const [silenceLeadQuiet, setSilenceLeadQuiet] = useState('');
  const [silenceGrace, setSilenceGrace] = useState('');
  const [silenceRemoteMax, setSilenceRemoteMax] = useState('');
  const [silenceLocalMin, setSilenceLocalMin] = useState('');
  const [note, setNote] = useState('');
  const [status, setStatus] = useState<string>('Loading…');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  function hydrate(s: AmdSettingsValue) {
    setSettings(s);
    setThreshold(String(s.amd_speech_threshold_ms));
    setEndThreshold(String(s.amd_speech_end_threshold_ms));
    setCardFallback(s.amd_card_fallback_ms === undefined ? '' : String(s.amd_card_fallback_ms));
    setLateMachineProtect(s.late_machine_protect_unmuted ?? true);
    setGreetingRecord(s.greeting_record_s === undefined ? '' : String(s.greeting_record_s));
    setAsyncOn(s.amd_async);
    setHoldUnrouted(s.hold_unrouted ?? false);
    setHoldDeadline(s.hold_deadline_ms === undefined ? '' : String(s.hold_deadline_ms));
    setHoldPerSdr(s.hold_per_sdr === undefined ? '' : String(s.hold_per_sdr));
    setOverdialPacing(pacingText(s.overdial_pacing_ms));
    setEasternOpen(s.eastern_open_hour === undefined ? '' : String(s.eastern_open_hour));
    setSilenceMode(s.silence_gate_mode ?? 'off');
    setSilenceFloor(s.silence_floor_ms === undefined ? '' : String(s.silence_floor_ms));
    setSilenceQuiet(s.silence_quiet_ms === undefined ? '' : String(s.silence_quiet_ms));
    setSilenceLeadQuiet(
      s.silence_lead_quiet_ms === undefined ? '' : String(s.silence_lead_quiet_ms),
    );
    setSilenceGrace(s.silence_grace_ms === undefined ? '' : String(s.silence_grace_ms));
    setSilenceRemoteMax(s.silence_remote_max === undefined ? '' : String(s.silence_remote_max));
    setSilenceLocalMin(s.silence_local_min === undefined ? '' : String(s.silence_local_min));
    setStatus('');
  }

  useEffect(() => {
    let live = true;
    getAmdSettings()
      .then((s) => {
        if (live) hydrate(s);
      })
      .catch((err: unknown) => {
        if (live) setStatus(err instanceof AdminRequestError ? err.message : 'Failed to load');
      });
    return () => {
      live = false;
    };
  }, []);

  // AMD_ASYNC is a load-bearing rollback lever: flipping it changes whether
  // voicemails are filtered synchronously at the bridge or asynchronously via the
  // /amd callback. A wrong flip can live-bridge voicemails to SDRs, so gate it.
  function onToggleAsync(next: boolean) {
    const message = next
      ? 'Enable ASYNC AMD?\n\nHumans bridge instantly and voicemails are filtered by Twilio’s async /amd callback. Only enable if HTTPS + the Twilio signature check are live, or voicemails can bridge to SDRs. Continue?'
      : 'Disable ASYNC AMD (switch to SYNCHRONOUS)?\n\nVoicemails are hung up before bridging (zero false cards) but real humans hear up to ~10s of dead air before connecting. Continue?';
    if (window.confirm(message)) setAsyncOn(next);
  }

  // Turning protection off gives up the guarantee that nothing hangs up on a live
  // person: a wrong `machine` verdict ends the call with no one able to save it. Worth
  // it when paired with a lowered fallback, but never a casual click.
  function onToggleLateMachineProtect(next: boolean) {
    if (next) {
      setLateMachineProtect(true);
      return;
    }
    const message =
      'Turn OFF late-machine protection?\n\nA machine verdict arriving after the SDR is unmuted will hang the call up automatically. That stops SDRs having to dismiss voicemails by hand, but it brings back the card flash and means a WRONG machine verdict cuts a live human with no way to save them. Continue?';
    if (window.confirm(message)) setLateMachineProtect(false);
  }

  // A core that predates a field cannot be sent one, so its control is hidden and its
  // value never validated or saved. Skipping the check matters as much as hiding the
  // input: an empty field is Number('') === 0, which fails the range test and would
  // block saving the knobs that core DOES support.
  const supportsCardFallback = settings?.amd_card_fallback_ms !== undefined;
  const supportsLateMachine = settings?.late_machine_protect_unmuted !== undefined;
  const supportsGreetingRecord = settings?.greeting_record_s !== undefined;
  // The hold fields ship in one migration, so one probe answers for the set.
  const supportsHold = settings?.hold_unrouted !== undefined;
  const supportsPacing = settings?.overdial_pacing_ms !== undefined;
  const supportsEasternOpen = settings?.eastern_open_hour !== undefined;
  // The six silence fields ship in one migration, so one probe answers for the set.
  const supportsSilenceGate = settings?.silence_gate_mode !== undefined;
  const silenceModes = settings?.silence_gate_modes ?? SILENCE_MODES_FALLBACK;

  // ON is destructive — the countdown expiring hangs calls up unattended — so
  // gate the flip like the async toggle.
  function onSelectSilenceMode(next: string) {
    if (next !== 'on') {
      setSilenceMode(next);
      return;
    }
    const message =
      'Turn the silence gate ON?\n\nWhen a carded call goes silent past a quiet window (both sides, or the lead alone for longer), the cockpit shows "Still on this call?" with a countdown and HANGS UP if the SDR does not react. Never flip this while the AMD threshold is also being tuned. Continue?';
    if (window.confirm(message)) setSilenceMode('on');
  }

  function clampError(): string | null {
    const t = Number(threshold);
    const e = Number(endThreshold);
    const f = Number(cardFallback);
    if (!Number.isInteger(t) || t < SPEECH_THRESHOLD.min || t > SPEECH_THRESHOLD.max)
      return `Speech threshold must be an integer ${SPEECH_THRESHOLD.min}–${SPEECH_THRESHOLD.max} ms.`;
    if (!Number.isInteger(e) || e < SPEECH_END.min || e > SPEECH_END.max)
      return `Speech-end threshold must be an integer ${SPEECH_END.min}–${SPEECH_END.max} ms.`;
    if (
      supportsCardFallback &&
      (!Number.isInteger(f) || f < CARD_FALLBACK.min || f > CARD_FALLBACK.max)
    )
      return `Card fallback must be an integer ${CARD_FALLBACK.min}–${CARD_FALLBACK.max} ms.`;
    if (supportsGreetingRecord) {
      const g = Number(greetingRecord);
      if (!Number.isInteger(g) || g < GREETING_RECORD.min || g > GREETING_RECORD.max)
        return `Greeting recording must be an integer ${GREETING_RECORD.min}–${GREETING_RECORD.max} s.`;
    }
    // Only when holding is ON: the inputs are hidden otherwise, and blocking Save on a
    // field nobody can see leaves the operator no way to fix it but to re-tick.
    if (supportsHold && holdUnrouted) {
      const hd = Number(holdDeadline);
      const hp = Number(holdPerSdr);
      if (!Number.isInteger(hd) || hd < HOLD_DEADLINE.min || hd > HOLD_DEADLINE.max)
        return `Hold deadline must be an integer ${HOLD_DEADLINE.min}–${HOLD_DEADLINE.max} ms.`;
      if (!Number.isInteger(hp) || hp < HOLD_PER_SDR.min || hp > HOLD_PER_SDR.max)
        return `Holds per SDR must be an integer ${HOLD_PER_SDR.min}–${HOLD_PER_SDR.max}.`;
    }
    if (supportsPacing) {
      // Blank is a value here, not a missing one: it turns pacing off.
      if (overdialPacing.trim() !== '') {
        const op = Number(overdialPacing);
        if (!Number.isInteger(op) || op < OVERDIAL_PACING.min || op > OVERDIAL_PACING.max)
          return `Overdial pacing must be blank (off) or an integer ${OVERDIAL_PACING.min}–${OVERDIAL_PACING.max} ms.`;
      }
    }
    if (supportsEasternOpen) {
      // Blank reads as 0, which the floor already rejects: there is no off state here.
      const eo = Number(easternOpen);
      if (!Number.isInteger(eo) || eo < EASTERN_OPEN.min || eo > EASTERN_OPEN.max)
        return `Eastern opening hour must be an integer ${EASTERN_OPEN.min}–${EASTERN_OPEN.max}.`;
    }
    if (supportsSilenceGate) {
      const sf = Number(silenceFloor);
      const sq = Number(silenceQuiet);
      const sg = Number(silenceGrace);
      const rm = Number(silenceRemoteMax);
      const lm = Number(silenceLocalMin);
      if (!Number.isInteger(sf) || sf < SILENCE_FLOOR.min || sf > SILENCE_FLOOR.max)
        return `Silence floor must be an integer ${SILENCE_FLOOR.min}–${SILENCE_FLOOR.max} ms.`;
      if (!Number.isInteger(sq) || sq < SILENCE_QUIET.min || sq > SILENCE_QUIET.max)
        return `Quiet window must be an integer ${SILENCE_QUIET.min}–${SILENCE_QUIET.max} ms.`;
      const slq = Number(silenceLeadQuiet);
      if (!Number.isInteger(slq) || slq < SILENCE_LEAD_QUIET.min || slq > SILENCE_LEAD_QUIET.max)
        return `Lead-quiet window must be an integer ${SILENCE_LEAD_QUIET.min}–${SILENCE_LEAD_QUIET.max} ms.`;
      if (!Number.isInteger(sg) || sg < SILENCE_GRACE.min || sg > SILENCE_GRACE.max)
        return `Countdown grace must be an integer ${SILENCE_GRACE.min}–${SILENCE_GRACE.max} ms.`;
      if (!Number.isFinite(rm) || rm <= 0 || rm >= 1)
        return 'Remote audibility threshold must be a number between 0 and 1 (exclusive).';
      if (!Number.isFinite(lm) || lm <= 0 || lm >= 1)
        return 'Local audibility threshold must be a number between 0 and 1 (exclusive).';
    }
    return null;
  }

  async function onSave() {
    setError(null);
    const invalid = clampError();
    if (invalid) {
      setError(invalid);
      return;
    }
    setSaving(true);
    try {
      const patch: AmdSettingsPatch = {
        amd_async: asyncOn,
        amd_speech_threshold_ms: Number(threshold),
        amd_speech_end_threshold_ms: Number(endThreshold),
      };
      if (supportsCardFallback) patch.amd_card_fallback_ms = Number(cardFallback);
      if (supportsLateMachine) patch.late_machine_protect_unmuted = lateMachineProtect;
      if (supportsGreetingRecord) patch.greeting_record_s = Number(greetingRecord);
      if (supportsHold) {
        patch.hold_unrouted = holdUnrouted;
        // Unvalidated while holding is off, so unsent — an unedited field would round-trip
        // its own value and an edited one would be rejected by a check the form skipped.
        if (holdUnrouted) {
          patch.hold_deadline_ms = Number(holdDeadline);
          patch.hold_per_sdr = Number(holdPerSdr);
        }
      }
      if (supportsPacing)
        patch.overdial_pacing_ms = overdialPacing.trim() === '' ? null : Number(overdialPacing);
      if (supportsEasternOpen) patch.eastern_open_hour = Number(easternOpen);
      if (supportsSilenceGate) {
        patch.silence_gate_mode = silenceMode;
        patch.silence_floor_ms = Number(silenceFloor);
        patch.silence_quiet_ms = Number(silenceQuiet);
        patch.silence_lead_quiet_ms = Number(silenceLeadQuiet);
        patch.silence_grace_ms = Number(silenceGrace);
        patch.silence_remote_max = Number(silenceRemoteMax);
        patch.silence_local_min = Number(silenceLocalMin);
      }
      if (note.trim()) patch.note = note.trim();
      await updateAmdSettings(patch);
      setNote('');
      onClose();
    } catch (err) {
      setError(err instanceof AdminRequestError ? err.message : String(err));
      setSaving(false);
    }
  }

  // Twilio cannot verdict before it has heard SpeechThreshold + SpeechEnd, so this is
  // the floor a card fallback has to clear to be a verdict rather than a guess. Read
  // off the live form, not the shipped defaults — both inputs sit right below it.
  const verdictFloorMs = Number(threshold) + Number(endThreshold) || 0;

  const dirty =
    settings !== null &&
    (asyncOn !== settings.amd_async ||
      threshold !== String(settings.amd_speech_threshold_ms) ||
      endThreshold !== String(settings.amd_speech_end_threshold_ms) ||
      (supportsCardFallback && cardFallback !== String(settings.amd_card_fallback_ms)) ||
      (supportsLateMachine && lateMachineProtect !== settings.late_machine_protect_unmuted) ||
      (supportsGreetingRecord && greetingRecord !== String(settings.greeting_record_s)) ||
      (supportsHold &&
        (holdUnrouted !== settings.hold_unrouted ||
          holdDeadline !== String(settings.hold_deadline_ms) ||
          holdPerSdr !== String(settings.hold_per_sdr))) ||
      (supportsPacing && overdialPacing !== pacingText(settings.overdial_pacing_ms)) ||
      (supportsEasternOpen && easternOpen !== String(settings.eastern_open_hour)) ||
      (supportsSilenceGate &&
        (silenceMode !== settings.silence_gate_mode ||
          silenceFloor !== String(settings.silence_floor_ms) ||
          silenceQuiet !== String(settings.silence_quiet_ms) ||
          silenceLeadQuiet !== String(settings.silence_lead_quiet_ms) ||
          silenceGrace !== String(settings.silence_grace_ms) ||
          silenceRemoteMax !== String(settings.silence_remote_max) ||
          silenceLocalMin !== String(settings.silence_local_min))));

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        {...dialog}
        aria-label="Answering-machine detection"
        className="bg-card flex max-h-[90vh] w-[min(40rem,94vw)] flex-col gap-4 overflow-y-auto rounded-lg border p-5 shadow-xl focus:outline-none"
        onClick={(e) => e.stopPropagation()}
      >
        <div>
          <h2 className="text-base font-semibold">Answering-machine detection</h2>
          <p className="text-muted-foreground text-sm">
            Global voicemail-detection tuning. Applies to every pool on the next dial.
          </p>
        </div>

        {status && <p className="text-muted-foreground text-sm">{status}</p>}
        {error && (
          <div className="border-destructive/40 bg-destructive/5 text-destructive rounded-md border p-3 text-sm">
            {error}
          </div>
        )}

        {settings && (
          <>
            {/* Outside the grid below, whose explainer covers only the Twilio knobs. */}
            {supportsCardFallback && (
              <label className="block text-sm">
                <span className="font-medium">Card fallback (ms)</span>
                <input
                  type="number"
                  className={INPUT_CLASS}
                  min={CARD_FALLBACK.min}
                  max={CARD_FALLBACK.max}
                  step={100}
                  value={cardFallback}
                  onChange={(ev) => setCardFallback(ev.target.value)}
                />
                <span className="text-muted-foreground mt-1 block text-xs">
                  How long to wait for Twilio&rsquo;s verdict before assuming human and showing the
                  card anyway ({CARD_FALLBACK.min}–{CARD_FALLBACK.max}). Twilio cannot decide before
                  it has heard {verdictFloorMs || '—'} ms of audio (the two thresholds below), so
                  under that almost every card is a guess rather than a verdict.
                  {lateMachineProtect ? (
                    <>
                      {' '}
                      With late-machine protection ON (below), a guess that turns out to be a
                      voicemail is <b>not</b> hung up — the SDR is left to dismiss each one by hand.
                      Lower this only alongside turning that off.
                    </>
                  ) : (
                    <>
                      {' '}
                      With late-machine protection OFF, a guess that turns out to be a voicemail is
                      hung up automatically — the SDR sees the card flash and vanish mid-pitch.
                    </>
                  )}
                </span>
              </label>
            )}

            {supportsHold && (
              <div className="flex flex-col gap-3 rounded-md border p-3">
                <label className="flex items-start gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={holdUnrouted}
                    onChange={(ev) => setHoldUnrouted(ev.target.checked)}
                  />
                  <span>
                    <span className="font-medium">Hold answers that find every SDR busy</span>
                    <span className="text-muted-foreground mt-1 block text-xs">
                      Off, the contact is hung up on the moment they say hello. On, the line is held
                      in silence while an SDR is found: a voicemail is ended early on Twilio&rsquo;s
                      verdict having cost nobody anything, and anyone else waits out the deadline.
                      If nobody comes free they get the same hangup, later.
                    </span>
                  </span>
                </label>

                {holdUnrouted && (
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <label className="block text-sm">
                      <span className="font-medium">Hold deadline (ms)</span>
                      <input
                        type="number"
                        className={INPUT_CLASS}
                        min={HOLD_DEADLINE.min}
                        max={HOLD_DEADLINE.max}
                        step={500}
                        value={holdDeadline}
                        onChange={(ev) => setHoldDeadline(ev.target.value)}
                      />
                      <span className="text-muted-foreground mt-1 block text-xs">
                        The longest a contact is asked to sit in silence ({HOLD_DEADLINE.min}–
                        {HOLD_DEADLINE.max}), and so how long there is to find them an SDR.
                      </span>
                    </label>

                    <label className="block text-sm">
                      <span className="font-medium">Holds per SDR</span>
                      <input
                        type="number"
                        className={INPUT_CLASS}
                        min={HOLD_PER_SDR.min}
                        max={HOLD_PER_SDR.max}
                        step={1}
                        value={holdPerSdr}
                        onChange={(ev) => setHoldPerSdr(ev.target.value)}
                      />
                      <span className="text-muted-foreground mt-1 block text-xs">
                        How many a pool may carry at once, per SDR joined and not leaving (
                        {HOLD_PER_SDR.min}–{HOLD_PER_SDR.max}). Hitting this often means the pool is
                        dialling too hard, not that the limit is too low.
                      </span>
                    </label>
                  </div>
                )}
              </div>
            )}

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <label className="block text-sm">
                <span className="font-medium">Speech threshold (ms)</span>
                <input
                  type="number"
                  className={INPUT_CLASS}
                  min={SPEECH_THRESHOLD.min}
                  max={SPEECH_THRESHOLD.max}
                  step={100}
                  value={threshold}
                  onChange={(ev) => setThreshold(ev.target.value)}
                />
                <span className="text-muted-foreground mt-1 block text-xs">
                  Continuous speech to call it a machine ({SPEECH_THRESHOLD.min}–
                  {SPEECH_THRESHOLD.max}, Twilio default {SPEECH_THRESHOLD.default}). Lower risks
                  hanging up chatty humans.
                </span>
              </label>

              <label className="block text-sm">
                <span className="font-medium">Speech-end threshold (ms)</span>
                <input
                  type="number"
                  className={INPUT_CLASS}
                  min={SPEECH_END.min}
                  max={SPEECH_END.max}
                  step={100}
                  value={endThreshold}
                  onChange={(ev) => setEndThreshold(ev.target.value)}
                />
                <span className="text-muted-foreground mt-1 block text-xs">
                  Silence after speech to finalize ({SPEECH_END.min}–{SPEECH_END.max}, Twilio
                  default {SPEECH_END.default}). Lower can mis-call a voicemail greeting as human.
                </span>
              </label>
            </div>

            <div className="bg-muted/40 rounded-md border p-3 text-xs leading-relaxed">
              <p className="mb-2 font-medium">How Twilio decides human vs. voicemail</p>

              <p className="mb-1">
                <b>Speech threshold</b> — how long they talk nonstop before it's called a recording:
              </p>
              <ul className="text-muted-foreground mb-2 space-y-0.5">
                <li>
                  • Person: <i>“Hello?”</i> → short burst → <b>human</b>
                </li>
                <li>
                  • Voicemail: <i>“Hi, you've reached Jane…”</i> → keeps talking → <b>machine</b>
                </li>
                <li>• Too low → a chatty human gets cut off as a machine (hung up on).</li>
              </ul>

              <p className="mb-1">
                <b>Speech-end threshold</b> — how long a pause means they've finished, so Twilio
                decides:
              </p>
              <ul className="text-muted-foreground space-y-0.5">
                <li>
                  • Too low: a greeting’s <i>“Hi! …pause… you've reached…”</i> finalizes on the{' '}
                  <i>“Hi!”</i> → wrongly <b>human</b> (a voicemail flashes a card).
                </li>
                <li>
                  • Higher: waits through the pause, hears the whole greeting → correctly{' '}
                  <b>machine</b> (costs a real caller ~½s to connect).
                </li>
              </ul>

              <button
                type="button"
                className="text-foreground mt-2 underline underline-offset-2 hover:no-underline"
                onClick={() => {
                  setThreshold(String(SPEECH_THRESHOLD.default));
                  setEndThreshold(String(SPEECH_END.default));
                }}
              >
                Use Twilio defaults ({SPEECH_THRESHOLD.default} / {SPEECH_END.default})
              </button>
            </div>

            {supportsLateMachine && (
              <div className="border-destructive/40 bg-destructive/5 rounded-md border p-3">
                <label className="flex items-start gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="mt-0.5 size-4 cursor-pointer"
                    checked={lateMachineProtect}
                    onChange={(ev) => onToggleLateMachineProtect(ev.target.checked)}
                  />
                  <span>
                    <span className="flex items-center gap-1 font-medium">
                      <AlertTriangle className="size-3.5" /> Late-machine protection{' '}
                      {lateMachineProtect ? 'ON' : 'OFF'}
                    </span>
                    <span className="text-muted-foreground mt-0.5 block text-xs">
                      ON: once the SDR is unmuted, a machine verdict is ignored and the call stays
                      live — nothing hangs up on a real person, but voicemails reaching a card must
                      be dismissed by hand. OFF: the verdict hangs the call up automatically, which
                      restores the card flash and lets a wrong verdict cut a live human.
                    </span>
                  </span>
                </label>
              </div>
            )}

            {supportsGreetingRecord && (
              <label className="block text-sm">
                <span className="font-medium">Record the greeting after a machine verdict (s)</span>
                <input
                  type="number"
                  className={INPUT_CLASS}
                  min={GREETING_RECORD.min}
                  max={GREETING_RECORD.max}
                  step={1}
                  value={greetingRecord}
                  onChange={(ev) => setGreetingRecord(ev.target.value)}
                />
                <span className="text-muted-foreground mt-1 block text-xs">
                  0 hangs the lead up the moment a voicemail is detected, as today. Above 0, the SDR
                  is still freed at that moment, but the lead&rsquo;s leg keeps recording for up to
                  this many seconds (it stops 3 s after the greeting goes quiet). The recording is
                  stored and transcribed overnight; it is not sent to Leadflow. A mailbox whose beep
                  comes first keeps up to 3 s of silence. Nothing the SDR sees changes.
                </span>
              </label>
            )}

            <div className="border-destructive/40 bg-destructive/5 rounded-md border p-3">
              <label className="flex items-start gap-2 text-sm">
                <input
                  type="checkbox"
                  className="mt-0.5 size-4 cursor-pointer"
                  checked={asyncOn}
                  onChange={(ev) => onToggleAsync(ev.target.checked)}
                />
                <span>
                  <span className="flex items-center gap-1 font-medium">
                    <AlertTriangle className="size-3.5" /> Async AMD {asyncOn ? 'ON' : 'OFF'}
                  </span>
                  <span className="text-muted-foreground mt-0.5 block text-xs">
                    Rollback lever. ON = bridge humans instantly, filter voicemails via the async
                    /amd callback. OFF = synchronous gate (no false cards, but ~10s dead air for
                    humans). Only enable with HTTPS + the Twilio signature check live.
                  </span>
                </span>
              </label>
            </div>

            {/* Last, and boxed: everything above tunes how a verdict is reached, this tunes
                when calls are placed. Read as another AMD knob it would be judged against
                the wrong outcome. */}
            {(supportsPacing || supportsEasternOpen) && (
              <div className="flex flex-col gap-3 rounded-md border p-3">
                <span className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
                  Dial placement
                </span>
                {supportsPacing && (
                  <label className="block text-sm">
                    <span className="font-medium">Overdial pacing (ms)</span>
                    {/* The step base is `min`, so a coarser step would make every round
                      number a stepMismatch and mark the field invalid. */}
                    <input
                      type="number"
                      className={INPUT_CLASS}
                      min={OVERDIAL_PACING.min}
                      max={OVERDIAL_PACING.max}
                      step={1}
                      value={overdialPacing}
                      onChange={(ev) => setOverdialPacing(ev.target.value)}
                    />
                    <span className="text-muted-foreground mt-1 block text-xs">
                      How long a pool waits between placing the speculative calls its calls-per-SDR
                      adds beyond one per free SDR. The first call for a free SDR is never delayed.
                      Leave blank to place them all together, as pools did before this existed.
                      Raising it spreads out when people pick up, so fewer answers land at once than
                      there are SDRs to take them. The wait is per call and pool-wide, not per SDR:
                      a pool places at most one speculative call per interval however many SDRs are
                      free, so reaching full capacity takes free SDRs × (calls-per-SDR − 1)
                      intervals. Bigger pools therefore ramp more slowly at the same setting.
                    </span>
                  </label>
                )}
                {supportsEasternOpen && (
                  <label className="block text-sm">
                    <span className="font-medium">US Eastern opening hour</span>
                    <input
                      type="number"
                      className={INPUT_CLASS}
                      min={EASTERN_OPEN.min}
                      max={EASTERN_OPEN.max}
                      step={1}
                      value={easternOpen}
                      onChange={(ev) => setEasternOpen(ev.target.value)}
                    />
                    <span className="text-muted-foreground mt-1 block text-xs">
                      The lead's own local hour that US and Canadian Eastern leads start being
                      dialled at. The Central zones open an hour before that, never before 8am;
                      everywhere else opens at 8am, and every zone stops at 6pm local. Lower means
                      more US dialling earlier in the day, and less of the day left for the rest of
                      the world before their evening.
                    </span>
                  </label>
                )}
              </div>
            )}

            {/* AMD tunes what Twilio decides; this tunes what the SDR's own browser does
                with a call AMD already let through. Boxed apart so the two are never
                tuned as one knob — flips here must not share a window with AMD
                threshold changes (both move voicemail SDR-seconds). */}
            {supportsSilenceGate && (
              <div className="flex flex-col gap-3 rounded-md border p-3">
                <span className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
                  Cockpit silence gate
                </span>
                <label className="block text-sm">
                  <span className="font-medium">Mode</span>
                  <select
                    className={INPUT_CLASS}
                    value={silenceMode}
                    onChange={(ev) => onSelectSilenceMode(ev.target.value)}
                    data-testid="silence-gate-mode"
                  >
                    {silenceModes.map((m) => (
                      <option key={m} value={m}>
                        {m}
                      </option>
                    ))}
                  </select>
                  <span className="text-muted-foreground mt-1 block text-xs">
                    One switch. <b>off</b>: nothing. <b>on</b>: when a carded call goes silent past
                    a quiet window — both sides, or the lead alone for longer — the cockpit raises
                    “Still on this call?” with Continue / Voicemail / End call and a countdown, and
                    ends the call if the SDR does nothing. Delivered per card, so a change lands on
                    the next call. Never flip it while the AMD thresholds above are also being
                    tuned.
                  </span>
                </label>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <label className="block text-sm">
                    <span className="font-medium">Card floor (ms)</span>
                    <input
                      type="number"
                      className={INPUT_CLASS}
                      min={SILENCE_FLOOR.min}
                      max={SILENCE_FLOOR.max}
                      step={1000}
                      value={silenceFloor}
                      onChange={(ev) => setSilenceFloor(ev.target.value)}
                    />
                    <span className="text-muted-foreground mt-1 block text-xs">
                      Card age before the gate may fire — the throttle, and the dominant lever (the
                      gate fires the moment it is allowed to).
                    </span>
                  </label>
                  <label className="block text-sm">
                    <span className="font-medium">Quiet window (ms)</span>
                    <input
                      type="number"
                      className={INPUT_CLASS}
                      min={SILENCE_QUIET.min}
                      max={SILENCE_QUIET.max}
                      step={500}
                      value={silenceQuiet}
                      onChange={(ev) => setSilenceQuiet(ev.target.value)}
                    />
                    <span className="text-muted-foreground mt-1 block text-xs">
                      Tier 1: BOTH sides silent this long. Resettable by the SDR&rsquo;s own noise,
                      so it can be defeated by an open mic; fast on the quiet-and-absent. Below ~6s
                      it lands inside real conversational pauses.
                    </span>
                  </label>
                  <label className="block text-sm">
                    <span className="font-medium">Lead-quiet window (ms)</span>
                    <input
                      type="number"
                      className={INPUT_CLASS}
                      min={SILENCE_LEAD_QUIET.min}
                      max={SILENCE_LEAD_QUIET.max}
                      step={1000}
                      value={silenceLeadQuiet}
                      onChange={(ev) => setSilenceLeadQuiet(ev.target.value)}
                    />
                    <span className="text-muted-foreground mt-1 block text-xs">
                      Tier 2: the LEAD alone silent this long, whatever the SDR&rsquo;s mic is doing
                      — nothing the SDR does resets it. Keep it 30s+: a listening lead is a silent
                      lead, so short windows fire into live pitches.
                    </span>
                  </label>
                  <label className="block text-sm">
                    <span className="font-medium">Countdown grace (ms)</span>
                    <input
                      type="number"
                      className={INPUT_CLASS}
                      min={SILENCE_GRACE.min}
                      max={SILENCE_GRACE.max}
                      step={1000}
                      value={silenceGrace}
                      onChange={(ev) => setSilenceGrace(ev.target.value)}
                    />
                    <span className="text-muted-foreground mt-1 block text-xs">
                      The SDR's window to react — click, speak, or be spoken to — before the hangup.
                    </span>
                  </label>
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <label className="block text-sm">
                    <span className="font-medium">Remote audibility (0–1)</span>
                    <input
                      type="number"
                      className={INPUT_CLASS}
                      min={0.001}
                      max={0.999}
                      step={0.005}
                      value={silenceRemoteMax}
                      onChange={(ev) => setSilenceRemoteMax(ev.target.value)}
                    />
                    <span className="text-muted-foreground mt-1 block text-xs">
                      Lead level above this counts as sound. Live-tunable because the analysed
                      thresholds came from recordings, not the SDK's 0–1 scale — calibrate in
                      shadow, not by deploy.
                    </span>
                  </label>
                  <label className="block text-sm">
                    <span className="font-medium">Local audibility (0–1)</span>
                    <input
                      type="number"
                      className={INPUT_CLASS}
                      min={0.001}
                      max={0.999}
                      step={0.01}
                      value={silenceLocalMin}
                      onChange={(ev) => setSilenceLocalMin(ev.target.value)}
                    />
                    <span className="text-muted-foreground mt-1 block text-xs">
                      SDR mic level above this counts as sound.
                    </span>
                  </label>
                </div>
              </div>
            )}

            {/* Labels the SAVE, not any one knob, so it sits below them all. Optional by
                design: a settings change must never be blocked on naming it. */}
            <label className="block text-sm">
              <span className="font-medium">Note (optional)</span>
              <input
                type="text"
                className={INPUT_CLASS}
                maxLength={120}
                placeholder="e.g. p1 arm=2400/800"
                value={note}
                onChange={(ev) => setNote(ev.target.value)}
              />
              <span className="text-muted-foreground mt-1 block text-xs">
                Labels this change in the settings history, which is what a tuning readout groups
                by. Without one, the arms of a sweep can only be told apart by timestamp.
              </span>
            </label>
          </>
        )}

        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button size="sm" onClick={() => void onSave()} disabled={saving || !dirty}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>
    </div>
  );
}
